import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, BookOpen, Pencil, Check, X, Download, Clock, ClipboardList, Sparkles, ChevronDown,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CohortGradebook({ cohortId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { learnerId, assessmentId }
  const [editScore, setEditScore] = useState("");
  const [editMixed, setEditMixed] = useState({ ext_correct: "", ext_total: "" });
  const [saving, setSaving] = useState(false);

  // LEXP preliminary state
  const [lexpData, setLexpData] = useState(null);
  const [lexpLoading, setLexpLoading] = useState(true);
  const [editingLexpCell, setEditingLexpCell] = useState(null); // { learnerId, lexpId }
  const [editLexpScore, setEditLexpScore] = useState("");
  const [finalizing, setFinalizing] = useState(null); // key
  const [bulkFinalizing, setBulkFinalizing] = useState(false);
  const [lexpStatusFilter, setLexpStatusFilter] = useState("all"); // all | preliminary | finalized

  // Score Items modal state
  const [extItemOpen, setExtItemOpen] = useState(false);
  const [extItemCtx, setExtItemCtx] = useState(null); // { learnerId, learnerName, lexpId, lexpTitle }
  const [extItemList, setExtItemList] = useState([]);
  const [extItemIncorrect, setExtItemIncorrect] = useState(new Set());
  const [extItemLoading, setExtItemLoading] = useState(false);
  const [extItemSaving, setExtItemSaving] = useState(false);

  // Per-LEXP-per-learner drill down (cohort gradebook). Keyed by
  // `${learnerId}_${lexpId}` so each row remembers its own state.
  const [expandedLexpKey, setExpandedLexpKey] = useState(null);
  const [drillItems, setDrillItems] = useState({}); // { [key]: items[] }
  const [drillLoading, setDrillLoading] = useState(null); // key being fetched

  const toggleDrill = async (learnerId, lexpId) => {
    const k = `${learnerId}_${lexpId}`;
    if (expandedLexpKey === k) {
      setExpandedLexpKey(null);
      return;
    }
    setExpandedLexpKey(k);
    if (drillItems[k]) return; // cached
    setDrillLoading(k);
    try {
      const { data } = await axios.get(
        `${API}/learners/${learnerId}/gradebook/lexp/${lexpId}/items?_=${Date.now()}`,
        { withCredentials: true, headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
      );
      setDrillItems((prev) => ({ ...prev, [k]: data.items || [] }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[CohortGradebook] drill fetch failed", err?.response?.status);
      toast.error("Could not load items");
    } finally { setDrillLoading(null); }
  };

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/cohorts/${cohortId}/gradebook`, { withCredentials: true });
      setData(d);
    } catch { toast.error("Failed to load gradebook"); }
    finally { setLoading(false); }
  }, [cohortId]);

  const fetchLexpData = useCallback(async () => {
    setLexpLoading(true);
    try {
      // Cache-bust so a save → refetch never gets a stale response from
      // browser / proxy caches. Score state is the canonical truth on the
      // server right after a write, but a `Cache-Control: max-age=N` somewhere
      // in the chain can return the pre-save body and leave the row showing
      // "Awaiting scores".
      const { data: d } = await axios.get(
        `${API}/cohorts/${cohortId}/gradebook/lexps?_=${Date.now()}`,
        { withCredentials: true, headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
      );
      setLexpData(d);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[CohortGradebook] fetchLexpData failed", err?.response?.status, err?.response?.data);
      toast.error("Could not refresh LEXP scoring");
    }
    finally { setLexpLoading(false); }
  }, [cohortId]);

  useEffect(() => { fetchData(); fetchLexpData(); }, [fetchData, fetchLexpData]);

  const saveScore = async () => {
    if (!editingCell) return;
    const val = parseFloat(editScore);
    if (isNaN(val) || val < 0 || val > 100) { toast.error("Score must be 0–100"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/learners/${editingCell.learnerId}/gradebook`, {
        assessment_id: editingCell.assessmentId, score: val,
      }, { withCredentials: true });
      toast.success("Score updated");
      setEditingCell(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
    finally { setSaving(false); }
  };

  const saveMixedScore = async () => {
    if (!editingCell) return;
    const ec = parseFloat(editMixed.ext_correct);
    const et = parseFloat(editMixed.ext_total);
    if (isNaN(ec) || ec < 0) { toast.error("Ext Correct must be >= 0"); return; }
    if (isNaN(et) || et < 0) { toast.error("Ext Total must be >= 0"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/learners/${editingCell.learnerId}/gradebook`, {
        assessment_id: editingCell.assessmentId, ext_correct: ec, ext_total: et,
      }, { withCredentials: true });
      toast.success("Score updated");
      setEditingCell(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
    finally { setSaving(false); }
  };

  // ── LEXP preliminary handlers ──
  const saveLexpExternalScore = async (learnerId, lexpId, maxScore) => {
    const val = parseFloat(editLexpScore);
    if (isNaN(val) || val < 0 || val > (maxScore || 100)) { toast.error(`Score must be 0–${maxScore || 100}`); return; }
    try {
      await axios.put(`${API}/learners/${learnerId}/gradebook`, {
        lexp_id: lexpId, cohort_id: cohortId, external_score: val,
      }, { withCredentials: true });
      toast.success("Saved — click Finalize to release to learner");
      setEditingLexpCell(null);
      fetchLexpData();
    } catch (err) { toast.error(err?.response?.data?.detail || `Save failed (${err?.response?.status || "network"})`); }
  };

  const finalizeLexp = async (learnerId, lexpId) => {
    const key = `${learnerId}_${lexpId}`;
    setFinalizing(key);
    try {
      await axios.post(
        `${API}/learners/${learnerId}/gradebook/lexp/${lexpId}/finalize`,
        { cohort_id: cohortId },
        { withCredentials: true },
      );
      toast.success("Finalized — visible to learner");
      // Optimistic flip so the pending count decrements without waiting on
      // the GET refetch.
      setLexpData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: (prev.rows || []).map((r) => {
            const matchId = r.learner_id || r.learner_id;
            if (matchId !== learnerId) return r;
            const s = r.lexps?.[lexpId];
            if (!s) return r;
            return {
              ...r,
              lexps: { ...(r.lexps || {}), [lexpId]: { ...s, is_finalized: true, is_preliminary: false, scores_entered: true } },
            };
          }),
        };
      });
      await fetchLexpData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Finalize failed");
    } finally { setFinalizing(null); }
  };

  const openScoreItems = async (learnerId, learnerName, lexpId, lexpTitle) => {
    setExtItemCtx({ learnerId, learnerName, lexpId, lexpTitle });
    setExtItemOpen(true);
    setExtItemLoading(true);
    setExtItemIncorrect(new Set());
    try {
      const { data: d } = await axios.get(
        `${API}/learners/${learnerId}/gradebook/lexp/${lexpId}/external-items`,
        { withCredentials: true },
      );
      const list = d.items || [];
      setExtItemList(list);
      const seed = new Set(list.filter((it) => it.has_response && it.current_correct === false).map((it) => it.item_id));
      setExtItemIncorrect(seed);
    } catch { toast.error("Failed to load external items"); setExtItemOpen(false); }
    finally { setExtItemLoading(false); }
  };

  const toggleExtItemIncorrect = (itemId) => {
    setExtItemIncorrect((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  const saveScoreItems = async () => {
    if (!extItemCtx) return;
    setExtItemSaving(true);
    try {
      const { data: saveResp } = await axios.post(
        `${API}/learners/${extItemCtx.learnerId}/gradebook/lexp/${extItemCtx.lexpId}/external-items`,
        { cohort_id: cohortId, incorrect_item_ids: Array.from(extItemIncorrect) },
        { withCredentials: true },
      );
      toast.success(`Saved & finalized (${extItemIncorrect.size} marked incorrect)`);
      const targetLearner = extItemCtx.learnerId;
      const targetLexp = extItemCtx.lexpId;
      setExtItemOpen(false);
      setExtItemCtx(null);
      // ── Optimistic update: flip the row immediately to "Finalized" using
      //    the score doc returned by the POST so the UI never shows stale
      //    "Awaiting scores" while the GET refetch is in-flight. The
      //    authoritative refresh below reconciles any drift.
      const newScore = saveResp?.score || {};
      setLexpData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: (prev.rows || []).map((r) => {
            const matchId = r.learner_id || r.learner_id;
            if (matchId !== targetLearner) return r;
            const merged = {
              ...(r.lexps?.[targetLexp] || {}),
              is_finalized: true,
              is_preliminary: false,
              scores_entered: true,
              has_score: true,
              final_score: newScore.final_score ?? r.lexps?.[targetLexp]?.final_score,
              native_score: newScore.native_score ?? r.lexps?.[targetLexp]?.native_score,
              native_max: newScore.native_max ?? r.lexps?.[targetLexp]?.native_max,
              external_item_score: newScore.external_item_score ?? r.lexps?.[targetLexp]?.external_item_score,
              external_item_max: newScore.external_item_max ?? r.lexps?.[targetLexp]?.external_item_max,
              external_score: newScore.external_score ?? r.lexps?.[targetLexp]?.external_score,
              external_max: newScore.external_max ?? r.lexps?.[targetLexp]?.external_max,
            };
            return { ...r, lexps: { ...(r.lexps || {}), [targetLexp]: merged } };
          }),
        };
      });
      // ── Authoritative refresh — awaited so the loading spinner correctly
      //    bridges to the fresh response.
      await fetchLexpData();
      // Drop drill cache for this row so the expanded panel re-fetches with
      // the freshly saved Correct/Incorrect chips.
      setDrillItems((prev) => {
        const next = { ...prev };
        delete next[`${targetLearner}_${targetLexp}`];
        return next;
      });
      // Refresh the assessment matrix too — overall % depends on LEXP scores.
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setExtItemSaving(false); }
  };

  const bulkFinalizeCohort = async (preliminaryCount) => {
    if (!window.confirm(`Finalize ${preliminaryCount} preliminary LEXP score${preliminaryCount === 1 ? "" : "s"} across all learners in this cohort? External items with no response will default to correct. Learners will see scores immediately.`)) return;
    setBulkFinalizing(true);
    try {
      const { data: d } = await axios.post(
        `${API}/cohorts/${cohortId}/gradebook/finalize-all`,
        {},
        { withCredentials: true },
      );
      toast.success(`Finalized ${d.finalized_count} preliminary LEXP${d.finalized_count === 1 ? "" : "s"} for ${d.learners_affected} learner${d.learners_affected === 1 ? "" : "s"}`);
      // Optimistic flip for every preliminary pair so the pending count
      // drops to zero immediately.
      setLexpData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: (prev.rows || []).map((r) => {
            const newLexps = { ...(r.lexps || {}) };
            let touched = false;
            for (const [lid, s] of Object.entries(newLexps)) {
              if (s && s.is_preliminary) {
                newLexps[lid] = { ...s, is_preliminary: false, is_finalized: true, scores_entered: true };
                touched = true;
              }
            }
            return touched ? { ...r, lexps: newLexps } : r;
          }),
        };
      });
      await fetchLexpData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk finalize failed");
    } finally { setBulkFinalizing(false); }
  };

  const exportCSV = () => {
    if (!data) return;
    const assessments = data.assessments;
    const headers = ["First Name", "Last Name", "Email", "Status", ...assessments.map((a) => a.name)];
    const csvRows = [headers.join(",")];
    for (const row of data.rows) {
      const cells = [
        `"${row.first_name}"`, `"${row.last_name}"`, `"${row.email}"`, row.status,
        ...assessments.map((a) => {
          const s = row.scores[a.assessment_id];
          if (!s || !s.has_score) return "";
          return s.is_mixed ? s.total_score : (s.score ?? "");
        }),
      ];
      csvRows.push(cells.join(","));
    }
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cohort_gradebook_${cohortId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredAssessments = () => (data ? data.assessments : []);

  // Count every (learner × LEXP) pair the cohort has score data for.
  // Used as the "All" filter total — independent of preliminary/finalized
  // state so pairs that are neither (e.g., native-only LEXPs, or pairs where
  // scores have been entered but not yet finalized) still get counted.
  const totalPairCount = (() => {
    if (!lexpData) return 0;
    let n = 0;
    for (const row of lexpData.rows || []) {
      for (const m of lexpData.lexps || []) {
        const s = row.lexps?.[m.lexp_id];
        if (s && (s.has_score || s.is_preliminary || s.is_finalized || s.scores_entered || s.final_score != null)) n += 1;
      }
    }
    return n;
  })();

  // Count preliminary LEXP-learner pairs across the cohort
  const preliminaryPairCount = (() => {
    if (!lexpData) return 0;
    let n = 0;
    for (const row of lexpData.rows || []) {
      for (const m of lexpData.lexps || []) {
        const s = row.lexps?.[m.lexp_id];
        if (s && s.is_preliminary) n += 1;
      }
    }
    return n;
  })();

  // Count finalized LEXP-learner pairs across the cohort
  const finalizedPairCount = (() => {
    if (!lexpData) return 0;
    let n = 0;
    for (const row of lexpData.rows || []) {
      for (const m of lexpData.lexps || []) {
        const s = row.lexps?.[m.lexp_id];
        if (s && s.is_finalized) n += 1;
      }
    }
    return n;
  })();

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>;
  if (!data) return null;

  const assessments = filteredAssessments();

  return (
    <div className="flex flex-col gap-6" data-testid="cohort-gradebook">
      {/* Header — order 1 (always first) */}
      <div className="flex items-center justify-between gap-4 flex-wrap" style={{ order: 1 }}>
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
          <h3 className="font-body text-sm font-semibold text-white">Cohort Gradebook</h3>
          <Badge className="bg-white/5 text-[#6E7787] border-white/10 text-[10px]">{data.rows.length} learners</Badge>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={exportCSV} className="h-8 text-xs font-body border-white/10 text-[#A1A9B8] hover:text-white hover:bg-white/5 gap-1.5" data-testid="gb-export-csv">
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
        </div>
      </div>

      {/* ── Historical assessments — rendered as another scoring section
          using the same per-learner card layout as LEXP scoring. Learner
          rows are always expanded (no accordion) and each assessment
          exposes the same "Update Score" affordance. order:3 places this
          BELOW the LEXP scoring section. */}
      {data.assessments.length > 0 && data.rows.length > 0 && (
        <div className="space-y-4" style={{ order: 3 }} data-testid="cohort-gb-historical-section">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[#A1A9B8]" />
            <h3 className="font-body text-sm font-semibold text-white">Historical assessments</h3>
            <Badge className="bg-white/5 text-[#6E7787] border-white/10 text-[10px]" data-testid="cohort-gb-historical-count">
              {data.assessments.length} {data.assessments.length === 1 ? "assessment" : "assessments"}
            </Badge>
          </div>

          {data.rows.map((row) => (
            <div
              key={row.learner_id}
              className="rounded-xl overflow-hidden border"
              style={{ backgroundColor: "#ffffff", borderColor: "#e2e6ef" }}
              data-testid={`cohort-gb-historical-card-${row.learner_id}`}
            >
              {/* Learner header — mirrors the LEXP scoring section header. */}
              <div className="flex items-start sm:items-center justify-between gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#e2e6ef", backgroundColor: "#f8fafc" }}>
                <button
                  onClick={() => navigate(`/admin/learners/${row.learner_id}`)}
                  className="text-left min-w-0 flex-1 hover:opacity-80 transition-opacity"
                  data-testid={`cohort-gb-historical-name-${row.learner_id}`}
                >
                  <p className="text-sm font-body font-semibold truncate" style={{ color: "#062546" }}>
                    {row.first_name} {row.last_name}
                  </p>
                  <p className="text-[11px] font-body truncate" style={{ color: "#8896a7" }}>{row.email}</p>
                </button>
                {row.overall_score_pct != null && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg shrink-0"
                    style={{ backgroundColor: "#e6faf8", border: "1px solid #99f6e4" }}
                    data-testid={`cohort-gb-historical-overall-${row.learner_id}`}>
                    <span className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#0d5a52" }}>Overall</span>
                    <span className="text-sm font-heading font-bold tabular-nums whitespace-nowrap" style={{ color: "#0d9488" }}>{row.overall_score_pct}%</span>
                    <span className="text-[10px] font-body tabular-nums" style={{ color: "#0d5a52" }}>{row.overall_correct}/{row.overall_total}</span>
                  </div>
                )}
              </div>

              {/* Assessment rows for this learner — one per historical assessment. */}
              <div className="divide-y" style={{ borderColor: "#f0f2f7" }}>
                {assessments.map((a, idx) => {
                  const s = row.scores[a.assessment_id] || {};
                  const isEditing = editingCell?.learnerId === row.learner_id && editingCell?.assessmentId === a.assessment_id;
                  const displayScore = s.is_mixed ? s.total_score : s.score;
                  const doSave = () => s.is_mixed ? saveMixedScore() : saveScore();
                  const beginEdit = () => {
                    setEditingCell({ learnerId: row.learner_id, assessmentId: a.assessment_id });
                    if (s.is_mixed) {
                      setEditMixed({ ext_correct: String(s.external_correct ?? ""), ext_total: String(s.external_total ?? "") });
                    } else {
                      setEditScore(s.has_score ? String(displayScore ?? "") : "");
                    }
                  };
                  return (
                    <div
                      key={a.assessment_id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
                      style={{ borderTop: idx === 0 ? "none" : "1px solid #f0f2f7" }}
                      data-testid={`cohort-gb-historical-row-${row.learner_id}-${a.assessment_id}`}
                    >
                      <span className="w-4 shrink-0" />
                      <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body font-semibold"
                        style={{ backgroundColor: "transparent", color: "#8896a7", border: "1.5px solid #c5cbd6" }}>
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-[140px]">
                        <p className="text-sm font-body font-semibold" style={{ color: "#062546" }}>{a.name}</p>
                        {(a.linked_lexp_ids || []).length > 0 && (
                          <p className="text-[11px] font-body mt-0.5" style={{ color: "#0891b2" }}>Linked</p>
                        )}
                        {s.is_mixed && s.has_score && !isEditing && (
                          <p className="text-[11px] font-body mt-0.5 tabular-nums" style={{ color: "#8896a7" }}>
                            N:{s.native_correct ?? 0}/{s.native_total ?? 0} · E:{s.external_correct ?? 0}/{s.external_total ?? 0}
                          </p>
                        )}
                      </div>

                      {!isEditing && (
                        <span
                          className="font-bold shrink-0 text-right tabular-nums whitespace-nowrap min-w-[56px]"
                          style={{ color: s.has_score ? "#0d9488" : "#c5cbd6" }}
                          data-testid={`cohort-gb-historical-score-${row.learner_id}-${a.assessment_id}`}
                        >
                          {s.has_score ? `${displayScore}%` : "\u2014"}
                        </span>
                      )}

                      {isEditing ? (
                        s.is_mixed ? (
                          <div className="flex flex-wrap items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-body" style={{ color: "#8896a7" }}>Ext Correct</span>
                              <Input type="number" min="0" step="1" value={editMixed.ext_correct}
                                onChange={(ev) => setEditMixed({ ...editMixed, ext_correct: ev.target.value })}
                                onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingCell(null); }}
                                autoFocus className="h-8 text-xs text-center w-20 rounded-md"
                                style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                                data-testid={`cohort-gb-historical-input-ext-correct-${row.learner_id}-${a.assessment_id}`} />
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-body" style={{ color: "#8896a7" }}>Ext Total</span>
                              <Input type="number" min="0" step="1" value={editMixed.ext_total}
                                onChange={(ev) => setEditMixed({ ...editMixed, ext_total: ev.target.value })}
                                onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingCell(null); }}
                                className="h-8 text-xs text-center w-20 rounded-md"
                                style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                                data-testid={`cohort-gb-historical-input-ext-total-${row.learner_id}-${a.assessment_id}`} />
                            </div>
                            <button onClick={doSave} disabled={saving} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#0d9488", borderColor: "#99f6e4" }}>
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setEditingCell(null)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 shrink-0 basis-full sm:basis-auto justify-end">
                            <Input
                              type="number" min="0" max="100" step="0.01" value={editScore}
                              onChange={(ev) => setEditScore(ev.target.value)}
                              onKeyDown={(ev) => { if (ev.key === "Enter") doSave(); if (ev.key === "Escape") setEditingCell(null); }}
                              autoFocus
                              className="h-8 text-xs text-center flex-1 sm:flex-none sm:w-24 rounded-md"
                              style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                              data-testid={`cohort-gb-historical-input-${row.learner_id}-${a.assessment_id}`}
                            />
                            <button onClick={doSave} disabled={saving} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#0d9488", borderColor: "#99f6e4" }}>
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setEditingCell(null)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={beginEdit}
                          className="h-8 px-3 text-xs font-body shrink-0"
                          style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                          data-testid={`cohort-gb-historical-update-btn-${row.learner_id}-${a.assessment_id}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Update Score
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── LEXP scoring section (unified prelim + finalized with status filter) ──
          Primary UI per product decision: render BEFORE the historical
          assessments section. order:2 places this above the assessments
          accordion (which is order:3). */}
      {lexpLoading ? (
        <div className="flex justify-center py-6" style={{ order: 2 }}><Loader2 className="h-4 w-4 animate-spin text-[#6E7787]" /></div>
      ) : lexpData && (lexpData.lexps || []).length > 0 && (
        <div className="space-y-4" style={{ order: 2 }} data-testid="cohort-gb-lexp-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              {lexpStatusFilter === "finalized"
                ? <Check className="h-4 w-4 text-emerald-400" />
                : <Clock className="h-4 w-4 text-amber-400" />}
              <h3 className="font-body text-sm font-semibold text-white">LEXP scoring</h3>
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px]" data-testid="cohort-gb-prelim-count">
                {preliminaryPairCount} pending
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={lexpStatusFilter}
                onChange={(e) => setLexpStatusFilter(e.target.value)}
                data-testid="cohort-gb-status-filter"
                className="h-8 bg-[#0A0C10] border border-white/10 text-white text-xs font-body rounded-md px-2.5 focus:outline-none focus:border-white/20"
              >
                <option value="all">All ({totalPairCount})</option>
                <option value="preliminary">Preliminary ({preliminaryPairCount})</option>
                <option value="finalized">Finalized ({finalizedPairCount})</option>
              </select>
              {lexpStatusFilter !== "finalized" && preliminaryPairCount > 0 && (
                <Button
                  size="sm"
                  onClick={() => bulkFinalizeCohort(preliminaryPairCount)}
                  disabled={bulkFinalizing}
                  className="h-8 text-xs font-body gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="cohort-gb-finalize-all-btn"
                >
                  {bulkFinalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Finalize All ({preliminaryPairCount})
                </Button>
              )}
            </div>
          </div>

          {(lexpData.rows || []).map((row) => {
            // LEXPs for this learner, filtered by status. The "all" branch
            // intentionally accepts EVERY pair that has score data — including
            // pairs that are neither preliminary nor finalized (e.g., pure
            // native LEXPs, or pairs where scores were entered but not yet
            // finalized) so finalizing a row never makes the learner card
            // disappear from view.
            const learnerLexps = (lexpData.lexps || [])
              .map((m) => ({ m, s: row.lexps?.[m.lexp_id] }))
              .filter(({ s }) => {
                if (!s) return false;
                if (lexpStatusFilter === "preliminary") return s.is_preliminary;
                if (lexpStatusFilter === "finalized") return s.is_finalized;
                // "all" — show every pair that has score data of any kind.
                return s.has_score || s.is_preliminary || s.is_finalized || s.scores_entered || s.final_score != null;
              });
            if (learnerLexps.length === 0) return null;
            return (
              <div
                key={row.learner_id}
                className="rounded-xl overflow-hidden border"
                style={{ backgroundColor: "#ffffff", borderColor: "#e2e6ef" }}
                data-testid={`cohort-gb-learner-card-${row.learner_id}`}
              >
                {/* Learner header */}
                <div className="flex items-start sm:items-center justify-between gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#e2e6ef", backgroundColor: "#f8fafc" }}>
                  <button
                    onClick={() => navigate(`/admin/learners/${row.learner_id}`)}
                    className="text-left min-w-0 flex-1 hover:opacity-80 transition-opacity"
                    data-testid={`cohort-gb-learner-name-${row.learner_id}`}
                  >
                    <p className="text-sm font-body font-semibold truncate" style={{ color: "#062546" }}>
                      {row.first_name} {row.last_name}
                    </p>
                    <p className="text-[11px] font-body truncate" style={{ color: "#8896a7" }}>{row.email}</p>
                  </button>
                  {row.overall_score_pct != null && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg shrink-0"
                      style={{ backgroundColor: "#e6faf8", border: "1px solid #99f6e4" }}
                      data-testid={`cohort-gb-learner-overall-${row.learner_id}`}>
                      <span className="text-[10px] font-body font-semibold uppercase tracking-wider" style={{ color: "#0d5a52" }}>Overall</span>
                      <span className="text-sm font-heading font-bold tabular-nums whitespace-nowrap" style={{ color: "#0d9488" }}>{row.overall_score_pct}%</span>
                      <span className="text-[10px] font-body tabular-nums" style={{ color: "#0d5a52" }}>{row.overall_correct}/{row.overall_total}</span>
                    </div>
                  )}
                </div>

                {/* LEXP rows for this learner */}
                <div className="divide-y" style={{ borderColor: "#f0f2f7" }}>
                  {learnerLexps.map(({ m, s }, idx) => {
                    const key = `${row.learner_id}_${m.lexp_id}`;
                    const isEditing = editingLexpCell?.learnerId === row.learner_id && editingLexpCell?.lexpId === m.lexp_id;
                    const scoresEntered = !!s.scores_entered;
                    const hasNative = s.native_score != null && s.native_max != null;
                    const hasExtItem = s.external_item_score != null && s.external_item_max != null;
                    const hasExtAgg = s.external_score != null && s.external_max != null;
                    const drillKey = `${row.learner_id}_${m.lexp_id}`;
                    const isDrillOpen = expandedLexpKey === drillKey;
                    const drillCanOpen = (m.native_item_count || 0) + (m.external_item_count || 0) > 0;
                    const drillRows = drillItems[drillKey];
                    const isDrillLoading = drillLoading === drillKey;
                    const onEnter = () => {
                      if (m.has_external_items) {
                        openScoreItems(row.learner_id, `${row.first_name} ${row.last_name}`, m.lexp_id, m.title);
                      } else {
                        setEditingLexpCell({ learnerId: row.learner_id, lexpId: m.lexp_id });
                        setEditLexpScore(s.external_score != null ? String(s.external_score) : "");
                      }
                    };
                    return (
                      <div key={m.lexp_id} style={{ borderTop: idx === 0 ? "none" : "1px solid #f0f2f7" }}>
                      <div
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
                        data-testid={`cohort-gb-lexp-row-${row.learner_id}-${m.lexp_id}`}
                      >
                        {drillCanOpen ? (
                          <button
                            onClick={() => toggleDrill(row.learner_id, m.lexp_id)}
                            className="shrink-0 w-4 h-4 flex items-center justify-center transition-transform"
                            style={{ color: "#8896a7", transform: isDrillOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                            data-testid={`cohort-gb-drill-toggle-${row.learner_id}-${m.lexp_id}`}
                            aria-label="Show items"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        ) : <span className="w-4 shrink-0" />}
                        <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body font-semibold"
                          style={{ backgroundColor: isDrillOpen ? "#062546" : "transparent", color: isDrillOpen ? "#fff" : "#8896a7", border: isDrillOpen ? "none" : "1.5px solid #c5cbd6" }}>
                          {idx + 1}
                        </span>
                        <button
                          onClick={() => drillCanOpen && toggleDrill(row.learner_id, m.lexp_id)}
                          className="flex-1 min-w-[140px] text-left"
                          disabled={!drillCanOpen}
                          style={{ cursor: drillCanOpen ? "pointer" : "default" }}
                          data-testid={`cohort-gb-lexp-title-${row.learner_id}-${m.lexp_id}`}
                        >
                          <p className="text-sm font-body font-semibold" style={{ color: "#062546" }}>{m.title}</p>
                          {(m.native_item_count > 0 || m.external_item_count > 0) && (
                            <p className="text-[11px] font-body mt-0.5" style={{ color: "#8896a7" }}>
                              {[m.native_item_count > 0 ? `${m.native_item_count} native item${m.native_item_count === 1 ? "" : "s"}` : null,
                                m.external_item_count > 0 ? `${m.external_item_count} external item${m.external_item_count === 1 ? "" : "s"}` : null]
                                .filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </button>

                        {/* Unified Items breakdown — sums every scored source
                            (native + external_item + aggregate external_score)
                            into one combined "Items: earned/total" pill, same
                            format as the learner gradebook. */}
                        {!isEditing && (() => {
                          let earned = 0;
                          let total = 0;
                          if (hasNative) { earned += Number(s.native_score) || 0; total += Number(s.native_max) || 0; }
                          if (hasExtItem) { earned += Number(s.external_item_score) || 0; total += Number(s.external_item_max) || 0; }
                          if (hasExtAgg) { earned += Number(s.external_score) || 0; total += Number(s.external_max) || 0; }
                          if (total <= 0) return null;
                          const fmt = (n) => Number.isInteger(n) ? n : Math.round(n * 100) / 100;
                          return (
                            <div className="flex items-center gap-3 text-[11px] font-body shrink-0 tabular-nums basis-full sm:basis-auto order-3 sm:order-none flex-wrap" style={{ color: "#8896a7" }}>
                              <span data-testid={`cohort-gb-lexp-items-${row.learner_id}-${m.lexp_id}`}>
                                Items: <span style={{ color: "#062546" }}>{fmt(earned)}/{fmt(total)}</span>
                              </span>
                            </div>
                          );
                        })()}

                        {/* Score */}
                        <span
                          className="font-bold shrink-0 text-right tabular-nums whitespace-nowrap min-w-[56px]"
                          style={{ color: (s.final_score != null && s.is_finalized) ? "#0d9488" : "#c5cbd6" }}
                          data-testid={`cohort-gb-lexp-score-${row.learner_id}-${m.lexp_id}`}
                        >
                          {s.final_score != null ? `${s.final_score}%` : "\u2014"}
                        </span>

                        {/* Status / action — Edit → Finalize → Update Score */}
                        {s.is_finalized ? (
                          <div className="flex items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end order-4 sm:order-none">
                            <Badge className="text-[10px] gap-1 shrink-0" style={{ backgroundColor: "#e6faf8", color: "#0d9488", border: "1px solid #99f6e4" }}>
                              <Check className="h-2.5 w-2.5" /> Finalized
                            </Badge>
                            {m.has_external_scoring && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={onEnter}
                                className="h-8 px-3 text-xs font-body"
                                style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                data-testid={`cohort-gb-update-score-btn-${row.learner_id}-${m.lexp_id}`}
                              >
                                <Pencil className="h-3 w-3 mr-1" /> Update Score
                              </Button>
                            )}
                          </div>
                        ) : isEditing ? (
                          <div className="flex items-center gap-1 shrink-0 basis-full sm:basis-auto order-4 sm:order-none">
                            <Input
                              type="number" min="0" max={m.external_max || 100} step="0.01"
                              value={editLexpScore}
                              onChange={(ev) => setEditLexpScore(ev.target.value)}
                              onKeyDown={(ev) => { if (ev.key === "Enter") saveLexpExternalScore(row.learner_id, m.lexp_id, m.external_max); if (ev.key === "Escape") setEditingLexpCell(null); }}
                              autoFocus
                              className="h-8 text-xs text-center flex-1 sm:flex-none sm:w-24 rounded-md"
                              style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
                            />
                            <button onClick={() => saveLexpExternalScore(row.learner_id, m.lexp_id, m.external_max)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#0d9488", borderColor: "#99f6e4" }}>
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setEditingLexpCell(null)} className="h-7 w-7 rounded flex items-center justify-center border shrink-0" style={{ color: "#8896a7", borderColor: "#e2e6ef" }}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : m.has_external_scoring ? (
                          <div className="flex items-center gap-2 shrink-0 basis-full sm:basis-auto justify-end order-4 sm:order-none">
                            <span
                              className="text-xs font-body text-right"
                              style={{ color: "#8896a7" }}
                              data-testid={`cohort-gb-lexp-status-${row.learner_id}-${m.lexp_id}`}
                            >
                              {scoresEntered ? "Scores entered" : "Awaiting scores"}
                            </span>
                            {scoresEntered ? (
                              <>
                                {m.has_external_items && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onEnter}
                                    className="h-8 px-3 text-xs font-body"
                                    style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                    data-testid={`cohort-gb-edit-score-btn-${row.learner_id}-${m.lexp_id}`}
                                  >
                                    <Pencil className="h-3 w-3 mr-1" /> Edit Score
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => finalizeLexp(row.learner_id, m.lexp_id)}
                                  disabled={finalizing === key}
                                  className="h-8 px-3 text-xs font-body"
                                  style={{ borderColor: "#0d9488", color: "#0d9488", backgroundColor: "transparent" }}
                                  data-testid={`cohort-gb-finalize-btn-${row.learner_id}-${m.lexp_id}`}
                                >
                                  {finalizing === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" />Finalize</>}
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={onEnter}
                                className="h-8 px-3 text-xs font-body"
                                style={{ borderColor: "#c5cbd6", color: "#062546", backgroundColor: "transparent" }}
                                data-testid={`cohort-gb-enter-scores-btn-${row.learner_id}-${m.lexp_id}`}
                              >
                                <Pencil className="h-3 w-3 mr-1" /> Enter scores
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs font-body shrink-0" style={{ color: "#8896a7" }} data-testid={`cohort-gb-lexp-native-only-${row.learner_id}-${m.lexp_id}`}>
                            Native only
                          </span>
                        )}
                      </div>

                      {/* ── Per-LEXP per-learner item drilldown — same data
                          shape as the learner-detail gradebook so admins can
                          see Correct/Incorrect chips for every item (native
                          + external) without leaving the cohort view. ── */}
                      {isDrillOpen && (
                        <div className="px-4 pb-3 pt-1 ml-8" data-testid={`cohort-gb-drill-${row.learner_id}-${m.lexp_id}`}>
                          {isDrillLoading ? (
                            <div className="flex items-center gap-2 py-2 text-[11px] font-body" style={{ color: "#8896a7" }}>
                              <Loader2 className="h-3 w-3 animate-spin" /> Loading items…
                            </div>
                          ) : (drillRows && drillRows.length > 0) ? (
                            <div className="rounded-lg border divide-y" style={{ borderColor: "#e2e6ef", backgroundColor: "#f9fafc" }}>
                              {drillRows.map((it, ix) => {
                                const isExt = it.item_type === "external_item";
                                return (
                                  <div key={it.item_id} className="flex items-center gap-3 px-3 py-2"
                                    data-testid={`cohort-gb-drill-item-${row.learner_id}-${m.lexp_id}-${it.item_id}`}>
                                    <span className="text-[10px] font-body w-5 text-right shrink-0" style={{ color: "#8896a7" }}>{ix + 1}.</span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-body font-medium truncate" style={{ color: "#062546" }}>{it.title || "(untitled)"}</p>
                                      <p className="text-[10px] font-body" style={{ color: "#8896a7" }}>
                                        {isExt ? "External item" : "Native"}
                                        {it.is_scored ? ` · ${it.point_value} pt${it.point_value === 1 ? "" : "s"}` : " · unscored"}
                                      </p>
                                    </div>
                                    <span className="text-[11px] font-body tabular-nums shrink-0" style={{ color: "#8896a7" }}>
                                      {it.has_response ? `${it.earned_points ?? 0}/${it.point_value}` : "—"}
                                    </span>
                                    {!it.has_response ? (
                                      <span className="text-[10px] font-body shrink-0 px-2 py-0.5 rounded-full" style={{ color: "#8896a7", backgroundColor: "#eef0f5" }}>
                                        No response
                                      </span>
                                    ) : it.correct ? (
                                      <span className="text-[10px] font-body shrink-0 px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ color: "#0d9488", backgroundColor: "#e6faf8", border: "1px solid #99f6e4" }}>
                                        <Check className="h-2.5 w-2.5" /> Correct
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-body shrink-0 px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ color: "#b54343", backgroundColor: "#fde8e8", border: "1px solid #f5b7b7" }}>
                                        <X className="h-2.5 w-2.5" /> Incorrect
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-[11px] font-body py-2" style={{ color: "#8896a7" }}>No items found.</p>
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
{/* ── Score Items modal ── */}
      <Dialog open={extItemOpen} onOpenChange={(open) => { if (!open) { setExtItemOpen(false); setExtItemCtx(null); } }}>
        <DialogContent className="bg-white text-[#062546] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cohort-gb-ext-item-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: "#062546" }}>
              <ClipboardList className="h-5 w-5 text-teal-600" />
              Score External Items
            </DialogTitle>
            <DialogDescription className="text-sm font-body" style={{ color: "#5a6577" }}>
              {extItemCtx?.learnerName} · {extItemCtx?.lexpTitle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="p-3 rounded-lg text-xs font-body" style={{ backgroundColor: "#e6faf8", color: "#0d5a52", border: "1px solid #99f6e4" }}>
              <p className="font-medium mb-1">All items default to correct.</p>
              <p>Check only the items the learner got wrong. Saving will finalize the score and make it visible to the learner.</p>
            </div>
            {extItemLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-teal-600" /></div>
            ) : extItemList.length === 0 ? (
              <p className="text-sm font-body text-center py-4 text-[#8896a7]">No external items in this LEXP.</p>
            ) : (
              <div className="border rounded-lg divide-y" style={{ borderColor: "#e2e6ef" }}>
                {extItemList.map((it) => {
                  const marked = extItemIncorrect.has(it.item_id);
                  return (
                    <label
                      key={it.item_id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${marked ? "bg-red-50" : "hover:bg-gray-50"}`}
                      data-testid={`cohort-gb-ext-item-row-${it.item_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={marked}
                        onChange={() => toggleExtItemIncorrect(it.item_id)}
                        className="h-4 w-4 shrink-0"
                        style={{ accentColor: "#dc2626" }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-body font-medium truncate" style={{ color: "#062546" }}>{it.title || `Item ${it.item_id.slice(0, 6)}`}</p>
                        <p className="text-[11px] font-body text-[#8896a7]">{it.point_value} pt{it.point_value !== 1 ? "s" : ""}</p>
                      </div>
                      <span
                        className="text-[11px] font-body font-medium px-2 py-0.5 rounded-full shrink-0"
                        style={marked
                          ? { backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca" }
                          : { backgroundColor: "#e6faf8", color: "#0d9488", border: "1px solid #99f6e4" }
                        }
                      >
                        {marked ? "Incorrect" : "Correct"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {!extItemLoading && extItemList.length > 0 && (
              <div className="text-xs font-body text-[#5a6577]">
                {extItemList.length - extItemIncorrect.size} correct · {extItemIncorrect.size} incorrect
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setExtItemOpen(false)} className="text-[#5a6577]">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveScoreItems}
                disabled={extItemSaving || extItemLoading || extItemList.length === 0}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="cohort-gb-ext-item-save-btn"
              >
                {extItemSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                Save & Finalize
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
